<template>
  <BaseModal
    :heading="$t('website_service.dashboard.cancel_modal.title')"
    useForm
    small
    @submit="submit"
    @close="emit('close')"
  >
    <Flex
      direction="column"
      :gap="1.5"
    >
      <BaseText>
        {{ $t('website_service.dashboard.cancel_modal.description') }}
      </BaseText>
      <BaseDropdown
        required
        :options="cancellationReasons"
        :label="$t('website_service.dashboard.cancel_modal.reason')"
        v-model="cancellationReason"
        :visibleItemCount="5"
        v-test="'website-service-cancel-subscription-reason'"
      />
      <BaseInput
        v-if="cancellationReason === CancellationReasonEnum.Other"
        required
        type="textarea"
        :placeholder="$t('website_service.dashboard.cancel_modal.reason_explanation')"
        v-model="cancellationExplanation"
        v-test="'website-service-cancel-subscription-explanation'"
      />
    </Flex>

    <template #footer>
      <BaseButton
        color="inverted"
        @click="emit('close')"
        v-test="'website-service-cancel-subscription-close'"
      >
        {{ $t('website_service.dashboard.cancel_modal.secondary_action') }}
      </BaseButton>
      <BaseButton
        submitForm
        :loading="loading"
        v-test="'website-service-cancel-subscription-submit'"
      >
        {{ $t('website_service.dashboard.cancel_modal.primary_action') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import { computed, inject, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { CancellationReasonEnum, type Website } from '@/types';
import { REQUEST_WEBSITE_CANCELLATION } from '../../graphql';

const { t } = useI18n();

const { mutate, loading } = useMutation(REQUEST_WEBSITE_CANCELLATION);

const website = inject<Ref<Website>>('website');
const emit = defineEmits(['close']);

const cancellationReason = ref<CancellationReasonEnum | null>(null);
const cancellationExplanation = ref<string>('');

const cancellationReasons = computed(() => {
  const ordererdReasons = [
    CancellationReasonEnum.NotEnoughBookings,
    CancellationReasonEnum.TooExpensive,
    CancellationReasonEnum.SwitchingProvider,
    CancellationReasonEnum.ClosingBusiness,
    CancellationReasonEnum.Other,
  ];

  return ordererdReasons.map((value: CancellationReasonEnum) => ({
    label: t(
      `website_service.dashboard.cancel_modal.reasons.${value.toLowerCase()}`,
    ),
    value,
  }));
});

const submit = () => {
  mutate({
    input: {
      reason: cancellationReason.value,
      explanation: cancellationExplanation.value,
    },
  }).then(({ data }) => {
    website.value = {
      ...website.value,
      cancellationRequestedAt:
        data.requestWebsiteCancellation?.website?.cancellationRequestedAt,
      cancellationReason:
        data.requestWebsiteCancellation?.website?.cancellationReason,
      cancellationExplanation:
        data.requestWebsiteCancellation?.website?.cancellationExplanation,
    };
    emit('close');
  });
};
</script>
